// The mileage block (build pack 26/10/2026, pages 3–9): "Add mileage for this
// site visit" inside Log time, and the same block under the project picker in
// the standalone Add mileage dialog (Expenses › Add mileage, the Calendar's
// site-visit offer). The rules live in mileage-model.js; this draws them.
//
// Everything here is built with textContent / DOM nodes — the project's number
// and address are data, so none of it goes through innerHTML. The two icons are
// fixed markup.
import { createMileage } from '../mileage-model.js';
import { fmtMiles } from '../expense-maths.js';
import { drivingMiles } from '../db.js';
import { mileageRate, rateLabel, orgSetting } from '../org.js';

const ICON_OFFICE = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 14V4.5L8 2l5 2.5V14M6 14v-3h4v3M5.5 6.5h1M9.5 6.5h1M5.5 8.75h1M9.5 8.75h1" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>';
const ICON_PIN = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 14.5s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0c0 3.3 4.5 7.5 4.5 7.5Z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="7" r="1.6" fill="currentColor"/></svg>';
const ICON_WARN = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 2.2 14.5 13.5h-13L8 2.2Z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M8 6.5v3.2M8 11.3v.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
const ICON_PEN = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m3 13 .6-2.6L10.8 3.2l2 2-7.2 7.2L3 13Z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>';

let seq = 0;
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const linkBtn = (text, onClick) => { const b = el('button', 'mi-link', text); b.type = 'button'; b.addEventListener('click', onClick); return b; };
const money = (n) => `£${Number(n).toFixed(2)}`;

/** The site, as the route line labels it: "8601 site" over "22 Ashfield Road, Hinckley". */
function siteLabel(p) {
  if (!p) return { name: 'Site', addr: 'Pick a project' };
  const sa = p.siteAddress;
  const addr = typeof sa === 'string' ? sa : (sa && sa.line1) ? [sa.line1, sa.postcode].filter(Boolean).join(', ') : (p.site || '');
  return { name: `${p.number || ''} site`.trim(), addr: String(addr || '').trim() || 'No site address' };
}

/**
 * @param {object} o
 * @param {boolean} [o.withTick=true]  draw the "Add mileage" tick box (Log time); false = always on (standalone)
 * @param {function} [o.onChange]      called with the controller's view on every change
 * @param {function} [o.lookup]        the route lookup (db.js drivingMiles); injectable for the harness
 * @param {function} [o.onAddAddress]  "Add the site address" — opens the project
 */
export function mileageBlock({ withTick = true, onChange = () => {}, lookup = drivingMiles, onAddAddress = null, tickLabel = 'Add mileage for this site visit' } = {}) {
  const id = `mi-${++seq}`;
  const rate = mileageRate();
  const town = String(orgSetting('mileageOfficeLabel', '') || '');
  const root = el('div', 'mi');

  let tickBox = null;
  if (withTick) {
    const lab = el('label', 'mi-tick');
    tickBox = document.createElement('input');
    tickBox.type = 'checkbox'; tickBox.id = `${id}-tick`;
    lab.append(tickBox, el('span', null, tickLabel));
    root.appendChild(lab);
  }

  const block = el('div', 'mi-block'); block.hidden = withTick;
  // The route line: Office — site — Back. Decorative for a screen reader (the
  // live region below says the result in words).
  const route = el('div', 'mi-route'); route.setAttribute('aria-hidden', 'true');
  const end = (label) => { const e = el('div', 'mi-end'); const i = el('span', 'mi-ico'); i.innerHTML = ICON_OFFICE; e.append(i, el('b', null, label), el('span', 'mi-town', town)); return e; };
  const leg = () => { const l = el('div', 'mi-leg'); l.appendChild(el('span', 'mi-leg-lab')); return l; };
  const siteEnd = el('div', 'mi-end mi-site');
  const pin = el('span', 'mi-pin'); pin.innerHTML = ICON_PIN;
  const siteName = el('b'); const siteAddr = el('span', 'mi-town');
  siteEnd.append(pin, siteName, siteAddr);
  const legA = leg(); const legB = leg();
  route.append(end('Office'), legA, siteEnd, legB, end('Back'));

  const row = el('div', 'mi-row');
  const col = el('div', 'mi-col');
  const lab = el('label', 'tf-label', 'Miles, round trip'); lab.htmlFor = `${id}-miles`;
  const field = el('div', 'mi-field');
  const spin = el('span', 'mi-spin'); spin.setAttribute('aria-hidden', 'true');
  const input = document.createElement('input');
  input.id = `${id}-miles`; input.className = 'mi-in'; input.inputMode = 'decimal'; input.autocomplete = 'off';
  input.setAttribute('aria-describedby', `${id}-side`);
  const unit = el('span', 'mi-unit', 'mi');
  const tag = el('span', 'mi-tag');
  field.append(spin, input, unit, tag);
  col.append(lab, field);
  const side = el('div', 'mi-side'); side.id = `${id}-side`;
  row.append(col, side);

  const warn = el('div', 'mi-warn'); warn.hidden = true;
  const warnIco = el('span', 'mi-warn-ico'); warnIco.innerHTML = ICON_WARN;
  const warnText = el('span');
  warn.append(warnIco, warnText);
  const reasonWrap = el('div', 'mi-reason'); reasonWrap.hidden = true;
  const reasonLab = el('label', 'tf-label', 'Why the extra miles'); reasonLab.htmlFor = `${id}-why`;
  const reason = document.createElement('input');
  reason.id = `${id}-why`; reason.className = 'tf-box mi-why'; reason.maxLength = 300;
  reason.placeholder = 'e.g. via the builders’ merchant in Coalville';
  reasonWrap.append(reasonLab, reason);
  const note = el('div', 'mi-note'); note.hidden = true;
  const live = el('div', 'mi-live'); live.setAttribute('aria-live', 'polite'); live.setAttribute('role', 'status');
  block.append(route, row, warn, reasonWrap, note, live);
  root.appendChild(block);

  let lastPhase = null; let lastAnnounce = '';
  const ctl = createMileage({ lookup, rate, onChange: (v) => { paint(v); onChange(v); } });

  function paint(v) {
    block.hidden = withTick && !v.ticked;
    if (tickBox) tickBox.checked = v.ticked;
    const s = siteLabel(v.project);
    siteName.textContent = s.name; siteAddr.textContent = s.addr;
    const working = v.phase === 'working';
    const noRoute = v.phase === 'manual' && ['no-address', 'no-project'].includes(v.why);
    [legA, legB].forEach((l) => {
      l.classList.toggle('on', !!v.oneWay);
      l.classList.toggle('working', working);
      l.firstChild.textContent = v.oneWay ? `${fmtMiles(v.oneWay)} mi` : working ? 'working out…' : noRoute ? 'no address' : '';
    });

    // The field: locked with a spinner while it works; the tag says where the number came from.
    field.classList.toggle('locked', working);
    field.classList.toggle('over', v.tag === 'over' || v.tag === 'under');
    field.setAttribute('aria-busy', working ? 'true' : 'false');
    input.readOnly = working;
    input.setAttribute('aria-busy', working ? 'true' : 'false');
    input.placeholder = working ? 'Working it out…' : 'Miles';
    unit.hidden = working || !(v.miles > 0) && document.activeElement !== input;
    if (document.activeElement !== input || working) input.value = v.miles > 0 ? fmtMiles(v.miles) : '';
    tag.textContent = v.tag === 'manual' ? '' : v.tagText;
    tag.className = `mi-tag${v.tag && v.tag !== 'manual' ? ` mi-tag-${v.tag === 'under' ? 'over' : v.tag}` : ''}`;
    tag.hidden = !tag.textContent;
    if (v.tag === 'under') tag.textContent = 'Under';

    // Beside it.
    side.replaceChildren();
    const costLine = () => {
      if (v.cost == null) return;
      side.appendChild(el('div', 'mi-cost', money(v.cost)));
    };
    const caption = (...parts) => { const c = el('div', 'mi-cap'); parts.forEach((p) => c.append(p)); side.appendChild(c); };
    if (working) {
      caption('Office → site → back, by road');
      side.appendChild(linkBtn('Enter it yourself instead', () => { ctl.enterYourself(); input.focus(); }));
    } else if (v.phase === 'ready') {
      costLine();
      if (v.tag === 'route') caption(`at ${rateLabel(rate)} a mile · costed for you`);
      else {
        const d = Math.abs(v.diff);
        caption(`${fmtMiles(d)} mi ${v.diff > 0 ? 'more' : 'less'} than the route · `, linkBtn(`Use ${fmtMiles(v.computed)}`, () => { ctl.useRoute(); reason.value = ''; }));
      }
    } else if (v.phase === 'manual') {
      if (v.why === 'no-address') {
        caption('Track couldn’t work out the route');
        if (onAddAddress && v.project) side.appendChild(linkBtn('Add the site address', () => onAddAddress(v.project)));
      } else if (v.why === 'no-answer') caption('The route lookup didn’t answer.');
      else if (v.why === 'failed') caption('Track couldn’t work out the route.');
      else if (v.why === 'no-project') caption('Pick the project to work out the route.');
      if (v.cost != null) { costLine(); caption(`at ${rateLabel(rate)} a mile · entered by hand`); }
    }
    if (!rate && v.ticked) { side.replaceChildren(); caption('Your company has no mileage rate set yet, so mileage can’t be claimed. Ask the Owner to set one.'); }

    // Over or under: the flag message and the optional reason.
    const flagged = v.tag === 'over' || v.tag === 'under';
    warn.hidden = !flagged; reasonWrap.hidden = !flagged;
    if (flagged) {
      warnText.replaceChildren(el('b', null, 'Your approver will see this flagged. '),
        `More than 15% (or 5 miles) ${v.tag === 'over' ? 'over' : 'under'} the route. Say why, and the reason sits beside the flag.`);
      reasonLab.textContent = v.tag === 'over' ? 'Why the extra miles' : 'Why fewer miles';
    }

    // No site address / no answer: said plainly, and saved as entered by hand.
    const plain = v.phase === 'manual' && ['no-address', 'no-answer', 'failed'].includes(v.why);
    note.hidden = !plain;
    if (plain) {
      const ico = el('span', 'mi-note-ico'); ico.innerHTML = ICON_PEN;
      const t = el('span');
      if (v.why === 'no-address') t.append(el('b', null, `${(v.project && v.project.number) || 'This project'} has no site address`), ', so there’s no route to check against. Enter the round trip; it’s saved as entered by hand.');
      else t.append(el('b', null, 'The route lookup didn’t answer'), '. Enter the round trip; it’s saved as entered by hand.');
      note.replaceChildren(ico, t);
    }

    // The screen reader hears the result ("29.2 miles, from the route").
    if (v.announce && v.announce !== lastAnnounce) { live.textContent = v.announce; lastAnnounce = v.announce; }
    // Unlocked with no route: the field takes focus straight away.
    if (v.phase !== lastPhase && v.phase === 'manual' && v.ticked && ['no-address', 'no-answer', 'failed', 'yourself'].includes(v.why)) {
      setTimeout(() => { if (!block.hidden && document.contains(input)) input.focus(); }, 0);
    }
    lastPhase = v.phase;
  }

  input.addEventListener('input', () => ctl.typeMiles(input.value));
  input.addEventListener('blur', () => { const v = ctl.view(); if (v.miles > 0) input.value = fmtMiles(v.miles); unit.hidden = !(v.miles > 0); });
  reason.addEventListener('input', () => ctl.setReason(reason.value));
  if (tickBox) tickBox.addEventListener('change', () => ctl.tick(tickBox.checked));
  if (!withTick) ctl.tick(true);
  paint(ctl.view());

  return {
    el: root,
    ctl,
    view: () => ctl.view(),
    claim: () => ctl.claim(),
    setProject: (p) => ctl.setProject(p),
    tick: (on) => ctl.tick(on),
    focus: () => input.focus(),
    dispose: () => ctl.dispose(),
  };
}

/** A small spinner + words, for a dialog's footer note ("Waiting for the route"). */
export function footNote(text, { busy = true } = {}) {
  const w = el('span', 'mi-footnote');
  if (busy) { const s = el('span', 'mi-spin'); s.setAttribute('aria-hidden', 'true'); w.appendChild(s); }
  w.appendChild(el('span', null, text));
  return w;
}

/** The expense a mileage claim saves — mileage-model.js (pure, so the tests load it); kept here for its callers. */
export { mileageExpense } from '../mileage-model.js';
